/**
 * Stripe Payment Form Component
 */

import { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

interface PaymentFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  amount: number;
  currency: string;
  userEmail: string;
  userName?: string;
}

export default function PaymentForm({
  onSuccess,
  onError,
  amount,
  currency,
  userEmail,
  userName,
}: PaymentFormProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'error' | 'success' | 'info'>('error');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setMessage('Processing your payment...');
    setMessageType('info');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email: userEmail,
              name: userName || undefined,
            },
          },
          return_url: `${window.location.origin}/checkout`,
        },
        redirect: 'if_required',
      });

      if (error) {
        // Payment failed
        const errorMessage = error.message || 'An error occurred during payment';
        onError(errorMessage);
        setMessage(errorMessage);
        setMessageType('error');
        setIsProcessing(false);
      } else if (paymentIntent) {
        // Handle different payment statuses
        switch (paymentIntent.status) {
          case 'succeeded':
            // Payment successful!
            console.log('Payment succeeded, calling onSuccess callback');
            setPaymentSuccess(true);
            setMessage('✓ Payment successful! Redirecting to your orders...');
            setMessageType('success');
            // Wait 1 second before redirecting to show success message
            setTimeout(() => {
              console.log('Timeout complete, calling onSuccess');
              onSuccess();
            }, 1000);
            break;

          case 'processing':
            setMessage('Payment is processing. Please wait...');
            setMessageType('info');
            // Keep processing state, will be updated via webhook
            setTimeout(() => {
              setMessage('Payment is still processing. Check your dashboard for updates.');
              setMessageType('info');
              setIsProcessing(false);
            }, 5000);
            break;

          case 'requires_payment_method':
            setMessage('Payment failed. Please try another payment method.');
            setMessageType('error');
            setIsProcessing(false);
            break;

          default:
            setMessage(`Payment status: ${paymentIntent.status}. Please check your dashboard.`);
            setMessageType('info');
            setIsProcessing(false);
        }
      } else {
        setMessage('Unable to confirm payment status. Please check your dashboard.');
        setMessageType('error');
        setIsProcessing(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed';
      onError(errorMessage);
      setMessage(errorMessage);
      setMessageType('error');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Amount to pay:</span>
          <span className="text-xl font-bold text-primary-800 dark:text-primary-400">
            {currency.toUpperCase()} {amount.toFixed(2)}
          </span>
        </div>
      </div>

      {/* User Information (Read-only) */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Email:</strong> {userEmail}
        </p>
        {userName && (
          <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
            <strong>Name:</strong> {userName}
          </p>
        )}
      </div>

      <PaymentElement
        options={{
          layout: {
            type: 'tabs',
            defaultCollapsed: false,
          },
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
          defaultValues: {
            billingDetails: {
              email: userEmail,
              name: userName || '',
            },
          },
        }}
      />

      {message && (
        <div
          className={`rounded-lg p-4 ${
            messageType === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700'
              : messageType === 'info'
              ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700'
          }`}
        >
          <p
            className={`text-sm font-medium ${
              messageType === 'success'
                ? 'text-green-800 dark:text-green-400'
                : messageType === 'info'
                ? 'text-blue-800 dark:text-blue-400'
                : 'text-red-800 dark:text-red-400'
            }`}
          >
            {message}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing || paymentSuccess}
        className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed relative"
      >
        {paymentSuccess ? (
          <span className="flex items-center justify-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Payment Successful
          </span>
        ) : isProcessing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing Payment...
          </span>
        ) : (
          `Pay ${currency.toUpperCase()} ${amount.toFixed(2)}`
        )}
      </button>

      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
        <p>🔒 Your payment information is secure and encrypted</p>
        <p className="mt-1">Powered by Stripe</p>
      </div>
    </form>
  );
}
